import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart, Briefcase, Coins, Users, Plane, Flower2, Home as HomeIcon,
  Sun, MoreHorizontal, Sparkles, ShieldCheck, Clock, MessageCircle,
  Star, ArrowRight, CheckCircle2, FileText, TrendingUp, Search, BookOpen
} from 'lucide-react';
import { trackEvent } from '../../utils/analytics';
import { trackFunnelEvent } from '../../lib/poochoFunnelBeacon';
import { useLang } from '../../context/LanguageContext';

/**
 * PoochoLandingV2 — Consolidated CRO redesign for the Poocho Guruji browse
 * (landing) view. Matches the attached reference visual exactly:
 * cream/maroon/gold palette, split hero, 9-category grid, compact
 * testimonials, sample-answer showcase, media strip, final CTA band.
 *
 * IMPORTANT: This ONLY renders when `step === 'browse'`. All existing
 * functionality (question picker → form → Razorpay → answer → ritual →
 * WhatsApp delivery) is preserved because we still call the props
 * `onSelectQuestion` / `onAskCustom` supplied by the parent state machine.
 *
 * Props:
 *  - questions          : array of pre-mapped questions from backend
 *  - categoriesFromApi  : category taxonomy from backend
 *  - searchTerm         : lifted search state from parent
 *  - setSearchTerm      : setter
 *  - filtered           : parent-filtered question list (for "अन्य विशेष प्रश्न")
 *  - onSelectQuestion   : parent handler — moves flow to 'form' step
 *  - onAskCustom        : parent handler for custom typed question
 *  - onShowFullPicker   : parent handler that reveals+scrolls to legacy picker
 */

// Full transparent-bg cutout of Acharya Vivek — hosted in /public/brand.
// This version has the ENTIRE head + torso visible (सिर नहीं कटता), unlike
// the earlier customer-assets link which was pre-cropped at the forehead.
const ACHARYA_PORTRAIT = '/brand/vivek-cutout.png';

// 9 warm-coloured question categories (Reference visual: row of icon cards)
const NINE_CATEGORIES = [
  { key: 'marriage',   icon: Heart,          label_hi: 'विवाह / प्रेम',        sub_hi: 'कब होगा?',         color: '#E63946', bg: '#FDECEC', slugMatch: /vivah|marriage|shadi|love|prem/i },
  { key: 'career',     icon: Briefcase,      label_hi: 'नौकरी / करियर',       sub_hi: 'में बदलाव कब?',    color: '#B8942D', bg: '#FBF3DA', slugMatch: /career|naukri|job|promotion/i },
  { key: 'wealth',     icon: Coins,          label_hi: 'धन / व्यवसाय',         sub_hi: 'में वृद्धि होगी?',  color: '#C08B2C', bg: '#FCF1D6', slugMatch: /dhan|wealth|business|vyapar|paisa/i },
  { key: 'family',     icon: Users,          label_hi: 'परिवार / संतान',       sub_hi: 'से जुड़ा प्रश्न',    color: '#C2185B', bg: '#FDE6EE', slugMatch: /pariwar|santan|child|family/i },
  { key: 'foreign',    icon: Plane,          label_hi: 'विदेश यात्रा',          sub_hi: 'बनेगी या नहीं?',    color: '#7B1FA2', bg: '#F3E5F5', slugMatch: /videsh|foreign|abroad|travel/i },
  { key: 'health',     icon: Flower2,        label_hi: 'स्वास्थ्य',             sub_hi: 'कैसा रहेगा?',       color: '#E91E63', bg: '#FDE7EF', slugMatch: /swasth|health|arogya/i },
  { key: 'property',   icon: HomeIcon,       label_hi: 'घर / संपत्ति',          sub_hi: 'का योग',            color: '#C2185B', bg: '#FDE6EE', slugMatch: /ghar|property|makan|home/i },
  { key: 'remedies',   icon: Sun,            label_hi: 'नवग्रह / उपाय',        sub_hi: 'क्या करें?',         color: '#F57C00', bg: '#FFF3E0', slugMatch: /upay|graha|remedy|dosha/i },
  { key: 'other',      icon: MoreHorizontal, label_hi: 'कोई अन्य',              sub_hi: 'विशेष प्रश्न',      color: '#5D4037', bg: '#EFEBE9', slugMatch: null },
];

// Compact verified testimonials (real names from existing site data — not fabricated)
const COMPACT_TESTIMONIALS = [
  { name: 'Ritu Sharma', city: 'Delhi', text: '"मैंने विवाह से संबंधित प्रश्न पूछा था। 10 मिनट में इतना विस्तृत और सटीक उत्तर मिला कि मेरी चिंता दूर हो गई। बहुत धन्यवाद गुरुजी!"' },
  { name: 'Amit Verma', city: 'गुरुग्राम', text: '"विदेश यात्रा को लेकर सवाल था। जो समय बताया गया, उसी दौरान मेरा वीज़ा आ गया! बहुत प्रभावशाली विश्लेषण था।"' },
  { name: 'Pooja Mehta', city: 'Mumbai', text: '"स्वास्थ्य से जुड़ा प्रश्न पूछा था। उपाय सरल और प्रभावी थे। सेवा अत्यंत संतोषजनक है!"' },
];

const MEDIA_LOGOS = [
  { name: 'Zee News',     text: 'ZEE NEWS',        bg: '#E53935', fg: '#FFFFFF' },
  { name: 'Aaj Tak',      text: 'आज तक',           bg: '#C62828', fg: '#FFFFFF' },
  { name: 'News18',       text: 'NEWS 18',         bg: '#F57C00', fg: '#FFFFFF' },
  { name: 'India TV',     text: 'INDIA TV',        bg: '#1565C0', fg: '#FFFFFF' },
  { name: 'Times of India', text: 'THE TIMES OF INDIA', bg: '#0D47A1', fg: '#FFFFFF' },
  { name: 'Navbharat Times', text: 'NBT · Navbharat', bg: '#EF6C00', fg: '#FFFFFF' },
  { name: 'Dainik Bhaskar', text: 'दैनिक भास्कर',   bg: '#FDD835', fg: '#0A0A0A' },
];

export default function PoochoLandingV2({
  questions = [],
  categoriesFromApi = [],
  searchTerm = '',
  setSearchTerm,
  onSelectQuestion,
  onAskCustom,
  onShowFullPicker,
}) {
  const [heroInput, setHeroInput] = useState('');
  const { lang, toggleLang } = useLang();
  const isEN = lang === 'en';

  // Bilingual copy dictionary — single source of truth for hero + landing
  const T = {
    ribbon:   isEN ? 'Poocho Guruji · Vedic answer to every life question'
                   : 'पूछो गुरुजी · जीवन के हर प्रश्न का शास्त्रीय समाधान',
    ribbonShort: isEN ? 'Vedic solution' : 'शास्त्रीय समाधान',
    hLine1:   isEN ? 'The Vedic answer to your question' : 'आपके प्रश्न का ज्योतिषीय उत्तर',
    inMe:     isEN ? '' : 'में',
    features: [
      isEN ? 'Ask one question'        : 'एक प्रश्न पूछें',
      isEN ? 'Personal astro analysis' : 'व्यक्तिगत ज्योतिषीय विश्लेषण',
      isEN ? 'Answer on WhatsApp'      : 'WhatsApp पर उत्तर प्राप्त करें',
    ],
    placeholder: isEN ? 'Type your question here…' : 'अपना प्रश्न यहाँ लिखें…',
    cta:        isEN ? 'Ask your question for ₹99' : 'अपना प्रश्न ₹99 में पूछें',
    examples:   isEN
      ? 'e.g., When will I get my next promotion? | Is marriage on the cards? | Will I go abroad? | When will business flourish?'
      : 'उदाहरण: मेरी नौकरी कब लगेगी? | विवाह कब होगा? | विदेश यात्रा संभव है? | व्यापार में सफलता कब मिलेगी?',
    exampleLabel: isEN ? 'Examples:' : 'उदाहरण:',
    quote:      isEN ? '“Right direction · Better life”' : '“ सही दिशा · बेहतर जीवन ”',
    rightPills: [
      isEN ? 'Shastra-based'         : 'शास्त्रीय आधारित',
      isEN ? '100% Confidential'     : '100% गोपनीयता',
      isEN ? '33+ years experience'  : '33+ वर्षों का अनुभव',
    ],
    signature: {
      cursive: 'Vivek Mudgal',
      hindi:   isEN ? 'Acharya Vivek Mudgal' : 'आचार्य विवेक मुदगल',
      role:    isEN ? 'Astrologer · Vastu Expert · Spiritual Guide'
                    : 'ज्योतिषाचार्य · वास्तु विशेषज्ञ · आध्यात्मिक मार्गदर्शक',
    },
    trustBottom: [
      [isEN ? '33+ years'      : '33+ वर्ष',      isEN ? 'of experience'      : 'का अनुभव'],
      ['33,000+',              isEN ? 'happy clients'      : 'संतुष्ट ग्राहक'],
      ['4.9★',                 isEN ? 'Verified Reviews'   : 'Verified Reviews'],
      [isEN ? '100% Private'   : '100% गोपनीयता',  isEN ? 'Your data safe'    : 'आपकी जानकारी सुरक्षित'],
      ['Razorpay',             isEN ? 'Secure Payment'     : 'सुरक्षित भुगतान'],
      [isEN ? 'WhatsApp'       : 'WhatsApp पर',    isEN ? 'Instant answer'    : 'तुरंत उत्तर'],
    ],
    devotional: isEN ? 'Right direction · Right decision · Better life'
                     : 'सही दिशा · सही निर्णय · बेहतर जीवन',
    categoriesTitle: isEN ? 'What kind of questions can you ask?' : 'आप किस तरह के प्रश्न पूछ सकते हैं?',
    allTopics:       isEN ? 'See all topics'          : 'सभी विषय देखें',
    testimonialsTitle: isEN ? 'Our customers\' trust' : 'हमारे ग्राहकों का विश्वास',
    moreReviews:     isEN ? 'See more reviews'        : 'और समीक्षाएं देखें',
    sampleTitle:     isEN ? 'What kind of answer will you get?' : 'उत्तर कैसा मिलेगा?',
    sampleExampleTag: isEN ? '(example)'              : '(उदाहरण)',
    whatIncludedTitle: isEN ? 'What will your answer include?' : 'आपके उत्तर में क्या शामिल होगा?',
    mediaTitle:      isEN ? 'Featured in media' : 'मीडिया में हमारी चर्चा',
    finalCtaHeadPart1: isEN ? 'Why wait? '             : 'अब देर न करें — ',
    finalCtaHeadPart2: isEN ? 'ask your question now'  : 'अपना प्रश्न अभी पूछें',
    finalCtaSub:       isEN ? 'Get personal astrological guidance directly from Acharya Vivek ji'
                             : 'आचार्य विवेक जी से व्यक्तिगत ज्योतिषीय मार्गदर्शन प्राप्त करें',
    tenMin:            isEN ? 'in about 10 min' : 'लगभग 10 मिनट में',
    waAnswer:          isEN ? 'Answer on WhatsApp' : 'WhatsApp पर उत्तर',
  };

  // Pick a representative question for each of the 9 categories.
  // Matches by slugMatch regex, falls back to first available.
  const pickQuestionForCategory = (cat) => {
    if (!cat.slugMatch) return null; // "अन्य" → custom flow
    return questions.find(q =>
      cat.slugMatch.test(q.slug || '') ||
      cat.slugMatch.test(q.category || '') ||
      cat.slugMatch.test(q.question_hi || '')
    ) || null;
  };

  const handleCategoryClick = (cat) => {
    trackEvent('poocho_landing_category_click', {
      category_key: cat.key,
      source: 'landing_v2_grid',
    });
    if (cat.key === 'other') {
      // Focus hero input for custom question
      const inp = document.querySelector('[data-testid="pv2-hero-input"]');
      if (inp) {
        inp.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => inp.focus(), 400);
      }
      return;
    }
    const q = pickQuestionForCategory(cat);
    if (q && onSelectQuestion) {
      onSelectQuestion(q);
    } else if (onShowFullPicker) {
      onShowFullPicker();
    }
  };

  const submitHeroQuestion = () => {
    const q = heroInput.trim();
    if (q.length < 10) {
      // Focus + scroll to hero input for better UX
      const inp = document.querySelector('[data-testid="pv2-hero-input"]');
      if (inp) inp.focus();
      return;
    }
    trackEvent('poocho_landing_hero_submit', { source: 'hero_input', length: q.length });
    trackFunnelEvent('question_select', { meta: { slug: 'custom', source: 'landing_v2_hero', length: q.length } });
    if (setSearchTerm) setSearchTerm(q);
    if (onAskCustom) onAskCustom(q);
  };

  const scrollToPicker = () => {
    if (onShowFullPicker) onShowFullPicker();
    else {
      const el = document.getElementById('poocho-question-picker');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToHeroInput = () => {
    const inp = document.querySelector('[data-testid="pv2-hero-input"]');
    if (inp) {
      inp.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => inp.focus(), 400);
    }
  };

  return (
    <div data-testid="poocho-landing-v2" className="text-[#2c0b14]">
      {/* ═══ 1. HERO — EXACT match to attached reference visual ═══ */}
      <section
        className="relative overflow-hidden"
        style={{
          background: 'radial-gradient(ellipse at 70% 40%, #5a1a2e 0%, #3d101e 45%, #2c0b14 100%)',
        }}
        data-testid="pv2-hero"
      >
        {/* Ambient temple silhouette bottom-left (subtle) */}
        <div className="hidden md:block absolute left-0 bottom-0 w-[280px] h-[240px] opacity-[0.16] pointer-events-none" aria-hidden>
          <svg viewBox="0 0 280 240" className="w-full h-full">
            <path d="M0 240 L0 180 L20 180 L20 130 L40 130 L45 90 L55 90 L60 130 L80 130 L80 180 L100 180 L100 100 L120 80 L140 60 L160 80 L180 100 L180 180 L200 180 L200 130 L220 130 L225 90 L235 90 L240 130 L260 130 L260 180 L280 180 L280 240 Z" fill="#F4D477" />
          </svg>
        </div>
        {/* Big zodiac wheel behind photo (visible per reference) */}
        <div className="hidden md:block absolute right-[8%] top-1/2 -translate-y-1/2 opacity-[0.28] pointer-events-none" aria-hidden>
          <svg viewBox="0 0 500 500" width="560" height="560">
            <circle cx="250" cy="250" r="240" fill="none" stroke="#F1C40F" strokeWidth="1.5" />
            <circle cx="250" cy="250" r="200" fill="none" stroke="#F1C40F" strokeWidth="1.2" />
            <circle cx="250" cy="250" r="140" fill="none" stroke="#F1C40F" strokeWidth="1" />
            <circle cx="250" cy="250" r="80" fill="none" stroke="#F1C40F" strokeWidth="1" />
            {/* 12 zodiac spokes */}
            {Array.from({ length: 12 }).map((_, i) => {
              const a = (i * 30 * Math.PI) / 180;
              const x1 = 250 + Math.cos(a) * 80;
              const y1 = 250 + Math.sin(a) * 80;
              const x2 = 250 + Math.cos(a) * 240;
              const y2 = 250 + Math.sin(a) * 240;
              return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#F1C40F" strokeWidth="1" />;
            })}
          </svg>
        </div>
        {/* Top-right quote + language toggle */}
        <div className="hidden md:block absolute top-6 right-8 z-20 text-right" data-testid="pv2-hero-quote">
          <p className="font-heading text-[#F4D477] text-[15px] leading-tight italic">
            {T.quote.split(' · ').map((l, i, arr) => (
              <React.Fragment key={i}>
                {l}{i < arr.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        </div>
        {/* Language toggle — small pill top-right (visible on all viewports) */}
        <div className="absolute top-4 right-3 md:top-6 md:right-[220px] z-30" data-testid="pv2-lang-toggle-wrap">
          <button
            onClick={() => { toggleLang(); trackEvent('poocho_landing_lang_toggle', { to: isEN ? 'hi' : 'en' }); }}
            className="inline-flex items-center rounded-full text-[11px] font-semibold overflow-hidden shadow-lg"
            style={{ border: '1px solid rgba(244,196,54,0.5)' }}
            data-testid="pv2-lang-toggle-btn"
            aria-label="Toggle language"
          >
            <span className={`px-2.5 py-1 ${!isEN ? 'text-[#2c0b14]' : 'text-[#F4D477]'}`}
              style={!isEN ? { background: 'linear-gradient(90deg,#F4D477,#B8942D)' } : {}}
            >हिंदी</span>
            <span className={`px-2.5 py-1 ${isEN ? 'text-[#2c0b14]' : 'text-[#F4D477]'}`}
              style={isEN ? { background: 'linear-gradient(90deg,#F4D477,#B8942D)' } : {}}
            >EN</span>
          </button>
        </div>

        <div className="container mx-auto px-4 max-w-7xl relative">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-4 lg:gap-8 items-center pt-24 md:pt-28 pb-6 md:pb-10 lg:pb-14">
            {/* LEFT — headline, ₹99 badge, feature circles, CTA (mobile order-1) */}
            <div className="order-1 relative text-center lg:text-left">
              {/* Ribbon — plain text (no colored pill background) with Om on both sides.
                  Feb 2026: Removed the dark-red/purple gradient pill and border
                  per user request — only the Om + text remain, no colored base. */}
              <div className="inline-flex items-center gap-2 py-2 text-[12px] font-semibold tracking-wide mb-4 md:mb-5 mx-auto lg:mx-0"
                data-testid="pv2-hero-ribbon"
              >
                <span className="text-[#F4D477] text-base leading-none">🕉</span>
                <span className="text-[#F4D477] font-bold">{isEN ? 'Poocho Guruji' : 'पूछो गुरुजी'}</span>
                <span className="text-[#F8E5D5]/85">·</span>
                <span className="hidden sm:inline text-[#F8E5D5]">{T.ribbon.split(' · ')[1] || T.ribbon}</span>
                <span className="sm:hidden text-[#F8E5D5]">{T.ribbonShort}</span>
                <span className="text-[#F4D477] text-base leading-none">🕉</span>
              </div>

              {/* Primary headline — single elegant line
                  Font: Hindi → Noto Serif Devanagari (शास्त्रीय, premium ligatures)
                        English → Playfair Display (classical elegant serif).
                  Gold gradient text with subtle underline for premium feel. */}
              <h1
                className="font-heading font-bold text-white leading-[1.3] md:leading-[1.15] tracking-tight mb-4 md:mb-5"
                data-testid="pv2-hero-h1"
                style={{
                  fontSize: 'clamp(28px, 5.2vw, 56px)',
                  fontFamily: isEN
                    ? '"Playfair Display", Georgia, serif'
                    : '"Noto Serif Devanagari", "Noto Sans Devanagari", serif',
                  fontFeatureSettings: '"kern" 1, "liga" 1, "calt" 1',
                  fontOpticalSizing: 'auto',
                }}
              >
                <span className="block relative">
                  <span className="relative z-10 font-bold text-transparent bg-clip-text"
                    style={{
                      backgroundImage: 'linear-gradient(180deg,#FFF3B0 0%,#F4D477 40%,#B8942D 100%)',
                      fontFamily: 'inherit',
                    }}
                  >
                    {T.hLine1}
                  </span>
                  <span aria-hidden className="absolute left-0 right-0 -bottom-1 h-[3px] rounded-full opacity-70"
                    style={{ background: 'linear-gradient(90deg,transparent,#F4D477 20%,#F1C40F 50%,#F4D477 80%,transparent)' }}
                  />
                </span>
              </h1>

              {/* Ornate ₹99 badge — the heart of the hero */}
              <div className="mb-5 md:mb-6 flex justify-center lg:justify-start" data-testid="pv2-hero-99-badge">
                <div className="relative inline-flex items-center">
                  {/* Left flourish */}
                  <span aria-hidden className="hidden sm:block text-[#F4D477] text-3xl leading-none -mr-2 opacity-90">❖</span>
                  <div
                    className="relative px-7 sm:px-10 py-3 sm:py-4 rounded-full shadow-2xl"
                    style={{
                      background: 'linear-gradient(180deg,#FFE58A 0%,#F4D477 35%,#D4AF37 70%,#8B6914 100%)',
                      border: '3px solid #6B4423',
                      boxShadow: '0 8px 30px rgba(244,196,54,0.35), inset 0 2px 4px rgba(255,255,255,0.4), inset 0 -2px 6px rgba(0,0,0,0.25)',
                    }}
                  >
                    <span className="font-heading font-black text-[#2c0b14] leading-none tracking-tight" style={{ fontSize: 'clamp(36px, 6vw, 60px)', textShadow: '0 2px 0 rgba(255,255,255,0.35)' }}>
                      ₹99
                    </span>
                  </div>
                  {/* Right flourish */}
                  <span aria-hidden className="hidden sm:block text-[#F4D477] text-3xl leading-none -ml-2 opacity-90">❖</span>
                </div>
              </div>

              {/* Three LARGE circular feature badges — reference visual */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-5 md:mb-6 max-w-xl mx-auto lg:mx-0" data-testid="pv2-hero-feature-badges">
                <FeatureCircle icon={<Search className="w-5 h-5 sm:w-6 sm:h-6" />} label={T.features[0]} />
                <FeatureCircle icon={<Flower2 className="w-5 h-5 sm:w-6 sm:h-6" />} label={T.features[1]} />
                <FeatureCircle icon={<MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />} label={T.features[2]} green />
              </div>

              {/* Search input — standalone card (own container, mobile-friendly) */}
              <div
                className="rounded-full bg-white shadow-xl shadow-black/40 p-1.5 flex items-center gap-2 max-w-xl mx-auto lg:mx-0"
                data-testid="pv2-hero-input-wrap"
              >
                <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 ml-1" style={{ background: '#F4E4CC' }}>
                  <Search className="w-4 h-4 text-[#8B0000]" />
                </div>
                <input
                  type="text"
                  value={heroInput}
                  onChange={(e) => setHeroInput(e.target.value)}
                  placeholder={T.placeholder}
                  className="flex-1 min-w-0 bg-transparent outline-none text-[#2c0b14] placeholder:text-[#8B0000]/45 py-3 pr-4 text-[14.5px]"
                  onKeyDown={(e) => e.key === 'Enter' && submitHeroQuestion()}
                  data-testid="pv2-hero-input"
                />
              </div>

              {/* Primary CTA — STANDALONE, own row, obvious next step */}
              <div className="mt-3 md:mt-4 flex justify-center lg:justify-start" data-testid="pv2-hero-cta-wrap">
                <button
                  onClick={submitHeroQuestion}
                  className="group relative inline-flex items-center justify-center gap-2 w-full max-w-xl px-6 py-4 rounded-full font-bold text-white text-[15px] tracking-wide shadow-2xl active:scale-[0.98] transition-transform"
                  style={{
                    background: 'linear-gradient(135deg,#8B0000,#B71C1C 45%,#8B0000)',
                    border: '2px solid rgba(244,196,54,0.65)',
                    boxShadow: '0 10px 30px rgba(139,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.15)',
                  }}
                  data-testid="pv2-hero-cta"
                >
                  <span>{T.cta}</span>
                  <span className="w-7 h-7 rounded-full flex items-center justify-center transition-transform group-hover:translate-x-0.5"
                    style={{ background: 'linear-gradient(135deg,#F4D477,#B8942D)', color: '#2c0b14' }}
                  >
                    <ArrowRight className="w-4 h-4" strokeWidth={3} />
                  </span>
                </button>
              </div>
              <p className="text-[11px] sm:text-[12px] text-[#F8E5D5]/80 mt-3 pl-1">
                <span className="text-[#F4D477] font-semibold">{T.exampleLabel}</span>{' '}
                {T.examples.replace(/^(उदाहरण|e\.g\.,)\s*:?\s*/i, '')}
              </p>
            </div>

            {/* RIGHT — Full portrait + right-side pill badges + signature (mobile order-2) */}
            <div className="order-2 relative flex items-end justify-center lg:justify-end">
              <div className="relative w-full max-w-lg">
                {/* Portrait — FULL, NEVER cropped. Use max-w (not max-h) so
                    the image preserves its natural aspect ratio at every size
                    and सिर कभी नहीं कटता। */}
                <img
                  src={ACHARYA_PORTRAIT}
                  alt="आचार्य विवेक मुदगल — पूछो गुरुजी"
                  loading="eager"
                  decoding="async"
                  className="relative z-10 h-auto w-full max-w-[220px] sm:max-w-[320px] md:max-w-[440px] mx-auto drop-shadow-[0_25px_50px_rgba(0,0,0,0.6)]"
                  data-testid="pv2-hero-portrait"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />

                {/* Right-side stacked trust pill badges — matches reference (only on desktop, positioned OUTSIDE photo so head is never covered) */}
                <div className="hidden lg:flex absolute right-[-30px] top-[35%] flex-col gap-2.5 z-20" data-testid="pv2-hero-right-pills">
                  <RightPill icon={<BookOpen className="w-4 h-4" />}   label={T.rightPills[0]} />
                  <RightPill icon={<ShieldCheck className="w-4 h-4" />} label={T.rightPills[1]} />
                  <RightPill icon={<Users className="w-4 h-4" />}       label={T.rightPills[2]} />
                </div>

                {/* Signature block — matches reference */}
                <div className="relative z-10 -mt-1 md:-mt-3 text-center">
                  <p className="font-heading italic tracking-wide" style={{ color: '#F4D477', fontSize: 'clamp(20px, 3vw, 34px)', textShadow: '0 2px 6px rgba(0,0,0,0.4)' }}>
                    {T.signature.cursive}
                  </p>
                  <p className="text-[#F8E5D5] text-[11px] md:text-[13px] tracking-[0.15em] mt-0.5">{T.signature.hindi}</p>
                  <p className="hidden md:block text-[#F8E5D5]/75 text-[11px] mt-1">{T.signature.role}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom trust strip (6 pills) — matches reference visual */}
          <div className="pt-4 pb-3 md:pb-4 mt-4 md:mt-6 border-t border-[#F4D477]/15" data-testid="pv2-hero-bottom-trust">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3">
              <BottomTrust icon="🏆" primary={T.trustBottom[0][0]} secondary={T.trustBottom[0][1]} />
              <BottomTrust icon="👥" primary={T.trustBottom[1][0]} secondary={T.trustBottom[1][1]} />
              <BottomTrust icon="⭐" primary={T.trustBottom[2][0]} secondary={T.trustBottom[2][1]} />
              <BottomTrust icon="🛡" primary={T.trustBottom[3][0]} secondary={T.trustBottom[3][1]} />
              <BottomTrust icon="🔒" primary={T.trustBottom[4][0]} secondary={T.trustBottom[4][1]} />
              <BottomTrust icon={<MessageCircle className="w-4 h-4 text-[#25D366]" />} primary={T.trustBottom[5][0]} secondary={T.trustBottom[5][1]} />
            </div>
          </div>

          {/* Devotional band at the very bottom of hero — matches reference */}
          <div className="pb-4 md:pb-6 pt-2 flex items-center justify-center gap-3 md:gap-4 text-center">
            <span aria-hidden className="text-[#F4D477] text-lg md:text-xl">🕉</span>
            <span className="tracking-[0.25em] text-[#F4D477] text-[11px] md:text-[13px]">
              {T.devotional}
            </span>
            <span aria-hidden className="text-[#F4D477] text-lg md:text-xl">🕉</span>
          </div>
        </div>
      </section>

      {/* ═══ 2. (removed — reference hero already has 6-pill bottom trust) ═══ */}

      {/* ═══ 3. QUESTION CATEGORIES — 9 colored icon cards ═══ */}
      <section
        id="pv2-categories-section"
        className="py-10 md:py-14 scroll-mt-24"
        style={{ background: '#FFFFFF' }}
        data-testid="pv2-categories"
      >
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6 md:mb-8">
            <h2 className="font-heading text-2xl md:text-3xl text-[#2c0b14] leading-tight">
              आप किस तरह के <span style={{ color: '#8B0000' }}>प्रश्न पूछ सकते हैं?</span>
            </h2>
            <button
              onClick={scrollToPicker}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 text-[13px] font-semibold hover:bg-[#8B0000] hover:text-white transition-colors"
              style={{ borderColor: '#8B0000', color: '#8B0000' }}
              data-testid="pv2-all-topics-btn"
            >
              सभी विषय देखें <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-9 gap-3">
            {NINE_CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.key}
                  onClick={() => handleCategoryClick(cat)}
                  className="group relative flex flex-col items-center gap-2 p-3 md:p-4 rounded-2xl border transition-all hover:-translate-y-1 hover:shadow-lg"
                  style={{ borderColor: '#F0D9E0', background: '#FFFFFF' }}
                  data-testid={`pv2-cat-${cat.key}`}
                >
                  <div
                    className="w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: cat.bg }}
                  >
                    <Icon className="w-5 h-5 md:w-6 md:h-6" style={{ color: cat.color }} strokeWidth={2} />
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-[12.5px] md:text-[13px] leading-tight text-[#2c0b14]">
                      {cat.label_hi}
                    </div>
                    <div className="text-[11px] text-[#8B0000]/70 leading-tight mt-0.5">
                      {cat.sub_hi}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ 4. TESTIMONIALS — 3 compact cards ═══ */}
      <section className="py-10 md:py-14" style={{ background: '#FDF6EA' }} data-testid="pv2-testimonials">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
            <h2 className="font-heading text-2xl md:text-3xl text-[#2c0b14]">
              हमारे ग्राहकों का <span style={{ color: '#8B0000' }}>विश्वास</span>
            </h2>
            <Link
              to="/reviews"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 text-[13px] font-semibold hover:bg-[#8B0000] hover:text-white transition-colors"
              style={{ borderColor: '#8B0000', color: '#8B0000' }}
              onClick={(e) => { /* fallback anchor if route not present — safe: existing site has google review */ e.preventDefault(); window.open('https://g.page/r/CbtzPR3gc6gNEBM/review', '_blank'); }}
              data-testid="pv2-all-reviews"
            >
              और समीक्षाएं देखें <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {COMPACT_TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="rounded-2xl p-5 bg-white shadow-sm border"
                style={{ borderColor: '#F0D9E0' }}
                data-testid={`pv2-testi-${i}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white"
                    style={{ background: `linear-gradient(135deg,#8B0000,#C2185B)` }}
                  >
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-[13px] text-[#2c0b14]">— {t.name}, <span className="text-[#8B0000]">{t.city}</span></div>
                    <div className="flex items-center gap-0.5 mt-0.5">
                      {[0, 1, 2, 3, 4].map(k => <Star key={k} className="w-3 h-3 fill-[#F1C40F]" style={{ color: '#F1C40F' }} />)}
                    </div>
                  </div>
                </div>
                <p className="text-[13.5px] text-[#3D101E] leading-relaxed italic">{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 5. SAMPLE ANSWER SHOWCASE ═══ */}
      <section className="py-10 md:py-14" style={{ background: '#FFFFFF' }} data-testid="pv2-sample-answer">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="font-heading text-2xl md:text-3xl text-[#2c0b14] mb-6 md:mb-8">
            उत्तर कैसा <span style={{ color: '#8B0000' }}>मिलेगा?</span> <span className="text-[#8B0000]/60 text-lg font-normal">(उदाहरण)</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-6 md:gap-10 items-center">
            {/* Tablet mockup */}
            <div className="relative">
              <div
                className="rounded-3xl p-5 md:p-6 shadow-2xl border-4"
                style={{
                  borderColor: '#2c0b14',
                  background: 'linear-gradient(180deg,#FDF6EA 0%,#FBF3DA 100%)',
                }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: '#8B0000' }}>V</div>
                  <span className="font-heading font-semibold text-[#2c0b14]">ViratAstro</span>
                </div>
                <div className="space-y-3 text-[13.5px] text-[#2c0b14]">
                  <p><strong style={{ color: '#8B0000' }}>प्रश्न:</strong> मेरी नौकरी में प्रमोशन कब होगा?</p>
                  <p><strong style={{ color: '#8B0000' }}>उत्तर:</strong> आपकी कुंडली के दशा-गोचर और ग्रह स्थिति के आधार पर…</p>
                  <ul className="space-y-2 pt-1">
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#B8942D' }} /> ग्रह स्थिति विश्लेषण</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#B8942D' }} /> संभावित समय अवधि</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#B8942D' }} /> अनुकूल उपाय</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#B8942D' }} /> सावधानियाँ</li>
                  </ul>
                </div>
                <div
                  className="absolute -bottom-3 right-4 flex items-center gap-2 px-3 py-1.5 rounded-lg text-white text-[11px] font-bold shadow-lg"
                  style={{ background: '#8B0000' }}
                >
                  <FileText className="w-3.5 h-3.5" /> PDF · विस्तृत रिपोर्ट
                </div>
              </div>
            </div>

            {/* "आपके उत्तर में क्या शामिल होगा?" — right side */}
            <div>
              <h3 className="font-heading text-xl md:text-2xl text-[#2c0b14] mb-5">आपके उत्तर में क्या शामिल होगा?</h3>
              <div className="grid grid-cols-2 gap-4">
                <SampleAnswerBullet icon={<TrendingUp className="w-6 h-6" />} title="व्यक्तिगत ज्योतिषीय विश्लेषण" sub="(आपके जन्म विवरण के आधार पर)" />
                <SampleAnswerBullet icon={<Clock className="w-6 h-6" />} title="समय और संभावनाएं" sub="(शास्त्रीय गणना के साथ)" />
                <SampleAnswerBullet icon={<Sparkles className="w-6 h-6" />} title="उपाय और मार्गदर्शन" sub="(व्यावहारिक और सरल)" />
                <SampleAnswerBullet icon={<FileText className="w-6 h-6" />} title="स्पष्ट और सरल भाषा में उत्तर" sub="(समझने में आसान)" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 6. MEDIA STRIP ═══ */}
      <section className="py-10 md:py-12" style={{ background: '#F8EFE0' }} data-testid="pv2-media-strip">
        <div className="container mx-auto px-4 max-w-6xl">
          <h3 className="font-heading text-2xl text-[#2c0b14] mb-5">
            मीडिया में हमारी <span style={{ color: '#8B0000' }}>चर्चा</span>
          </h3>
          <div className="flex flex-wrap items-center gap-3 md:gap-4">
            {MEDIA_LOGOS.map((m) => (
              <div
                key={m.name}
                className="px-4 py-2 rounded-lg font-heading text-[13px] font-bold shadow-sm"
                style={{ background: m.bg, color: m.fg }}
                data-testid={`pv2-media-${m.name.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {m.text}
              </div>
            ))}
            <div className="ml-auto max-w-xs italic text-[13px] text-[#3D101E] pl-2 border-l-2" style={{ borderColor: '#8B0000' }}>
              &ldquo;शास्त्र आधारित ज्योतिष से जन-जन तक सही मार्गदर्शन&rdquo;
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 7. FINAL CTA BAND ═══ */}
      <section
        className="py-8 md:py-10"
        style={{ background: 'linear-gradient(90deg,#2c0b14 0%,#3d101e 50%,#5a1a2e 100%)' }}
        data-testid="pv2-final-cta"
      >
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
            <div className="min-w-0">
              <h3 className="font-heading text-xl md:text-2xl text-white leading-tight mb-1">
                अब देर न करें — <span style={{ color: '#F1C40F' }}>अपना प्रश्न अभी पूछें</span>
              </h3>
              <p className="text-[#F8E5D5] text-[13.5px]">
                आचार्य विवेक जी से सीधे और व्यक्तिगत ज्योतिषीय उत्तर प्राप्त करें
              </p>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2 text-[#F8E5D5] text-[13px]">
                <Clock className="w-4 h-4 text-[#F4D477]" />
                लगभग 10 मिनट में
              </div>
              <div className="flex items-center gap-2 text-[#F8E5D5] text-[13px]">
                <MessageCircle className="w-4 h-4 text-[#25D366]" />
                WhatsApp पर उत्तर
              </div>
              <button
                onClick={() => {
                  trackEvent('poocho_landing_final_cta_click', { source: 'final_cta_band' });
                  scrollToPicker();
                }}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-bold text-[#2c0b14] text-[14px] shadow-lg shadow-black/30 hover:scale-[1.03] active:scale-[0.98] transition-transform"
                style={{ background: 'linear-gradient(90deg,#F4D477,#F1C40F,#B8942D)' }}
                data-testid="pv2-final-cta-btn"
              >
                अपना प्रश्न ₹99 में पूछें
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ─────── small internal atoms (kept local to avoid file sprawl) ─────── */

function StarRow() {
  return (
    <div className="flex items-center gap-0.5">
      {[0, 1, 2, 3, 4].map(k => <Star key={k} className="w-3.5 h-3.5 fill-[#F1C40F]" style={{ color: '#F1C40F' }} />)}
    </div>
  );
}

/* Large circular feature badge — matches reference visual (3 pills below headline) */
function FeatureCircle({ icon, label, green = false }) {
  return (
    <div className="flex flex-col items-center gap-1.5 sm:gap-2 text-center">
      <div
        className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center shadow-lg"
        style={{
          background: green
            ? 'radial-gradient(circle at 30% 30%, #1F5C3D, #0F3323)'
            : 'radial-gradient(circle at 30% 30%, #5a1a2e, #2c0b14)',
          border: '2px solid rgba(244,196,54,0.55)',
          color: green ? '#25D366' : '#F4D477',
        }}
      >
        {icon}
      </div>
      <span className="text-[10.5px] sm:text-[11.5px] text-[#F8E5D5] leading-tight max-w-[95px]">
        {label}
      </span>
    </div>
  );
}

/* Right-side vertical trust pill (photo edge) — matches reference visual */
function RightPill({ icon, label }) {
  return (
    <div
      className="inline-flex items-center gap-2 pl-3 pr-4 py-2 rounded-full shadow-lg backdrop-blur-sm"
      style={{
        background: 'linear-gradient(90deg,rgba(44,11,20,0.92),rgba(74,21,32,0.92))',
        border: '1px solid rgba(244,196,54,0.45)',
      }}
    >
      <span
        className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
        style={{ background: 'linear-gradient(135deg,#F4D477,#B8942D)', color: '#2c0b14' }}
      >
        {icon}
      </span>
      <span className="text-white text-[12px] font-semibold whitespace-nowrap">{label}</span>
    </div>
  );
}

/* Bottom 6-pill trust strip inside hero — matches reference visual */
function BottomTrust({ icon, primary, secondary }) {
  return (
    <div className="flex items-center gap-2 md:gap-2.5 rounded-xl py-2 px-2 md:px-3 bg-white/[0.06] border border-[#F4D477]/20">
      <div className="w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center shrink-0 text-base md:text-lg"
        style={{ background: 'linear-gradient(135deg,#F4D477,#B8942D)', color: '#2c0b14' }}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <div className="font-bold text-[#F4D477] leading-tight text-[11px] md:text-[13px] truncate">{primary}</div>
        <div className="text-[#F8E5D5]/80 leading-tight text-[9.5px] md:text-[11px] truncate">{secondary}</div>
      </div>
    </div>
  );
}

function TrustPill({ icon, primary, secondary, iconBg = '#FCF1D6', small = false }) {
  return (
    <div className={`flex items-center gap-3 rounded-xl bg-white border ${small ? 'py-2 px-3' : 'py-3 px-4'}`} style={{ borderColor: '#F0D9E0' }}>
      <div
        className={`shrink-0 rounded-full flex items-center justify-center ${small ? 'w-9 h-9 text-lg' : 'w-11 h-11 text-xl'}`}
        style={{ background: iconBg }}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <div className={`font-bold text-[#2c0b14] leading-tight ${small ? 'text-[13px]' : 'text-[14px]'}`}>{primary}</div>
        <div className={`text-[#8B0000]/80 leading-tight ${small ? 'text-[10.5px]' : 'text-[11.5px]'}`}>{secondary}</div>
      </div>
    </div>
  );
}

function SampleAnswerBullet({ icon, title, sub }) {
  return (
    <div className="flex flex-col items-start gap-2">
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center shadow-sm"
        style={{ background: '#FDECEC', color: '#8B0000' }}
      >
        {icon}
      </div>
      <div>
        <div className="font-semibold text-[13.5px] text-[#2c0b14] leading-tight">{title}</div>
        <div className="text-[11.5px] text-[#8B0000]/75 leading-tight mt-0.5">{sub}</div>
      </div>
    </div>
  );
}
